/**
 * Biomarker status engine (member-facing).
 *
 * Given a raw lab record and its catalogue entry, decide whether the value sits
 * in / above / below the reference range, in the SAME canonical unit the engine
 * uses. Unit handling mirrors the engine's `normaliseValue` (biomarkerRegistry.js)
 * so the member view and the decision engine never disagree about "in range".
 *
 * When the value is non-numeric, or its unit can't be reconciled to canonical
 * (e.g. free testosterone printed in nmol/L, which has no converter), we return
 * `status: 'unknown'` with `hasBar: false` — the UI then shows the value alone,
 * never a misplaced marker.
 */

import type { BiomarkerMeta, RefRange } from '../data/biomarkerCatalogue';
import { resolveMelioRefCandidates } from '../data/biomarkerCatalogue';

export type BiomarkerStatus = 'in_range' | 'high' | 'low' | 'unknown';

/**
 * Which reference range produced the verdict.
 *
 * PRODUCT DECISION (2026-08-25): Melio's ranges trump the performing lab's.
 * They are preventive-medicine targets and are deliberately tighter than a lab's
 * "not diseased" interval in places (triglycerides 1.7 vs 2.3, non-HDL 3.8 vs
 * 4.0, RDW 14.5 vs 15) — showing the member the tighter number is the product,
 * not a bug.
 *
 * The lab's range is the FALLBACK, used only where Melio has none that can be
 * applied to this member: no registry entry, a range describing the other sex,
 * or a unit we cannot convert (folate nmol/L vs µg/L, Active B12 pmol/L vs
 * ng/L). Before this, those cases produced no verdict at all and the member read
 * a bare number — even though their own lab report printed a range we were
 * holding in the record the whole time.
 */
export type RangeSource = 'melio' | 'lab';

export interface BiomarkerStatusResult {
  status: BiomarkerStatus;
  label: string;
  /** Semantic colour for badge text / marker fill. */
  color: string;
  /** Soft background for a status pill. */
  softBackground: string;
  /** Whether a range bar can be drawn (numeric value + reconciled unit + a range). */
  hasBar: boolean;
  /** Canonical-unit numeric value used for the comparison (null when unusable). */
  canonicalValue: number | null;
  /** The sex-resolved reference range, in whichever unit `rangeSource` implies. */
  ref: RefRange;
  /** Whose range `ref` is, or null when no verdict was reached. */
  rangeSource: RangeSource | null;
  /**
   * Axis bounds + unit the bar must be drawn in. For a Melio range this is the
   * catalogue's canonical scale; for a lab range it is derived from the lab's
   * own numbers, because the catalogue axis is in the canonical unit and
   * plotting a nmol/L value on a µg/L axis puts the marker in the wrong place.
   */
  axisMin: number;
  axisMax: number;
  axisUnit: string;
  /**
   * The performing lab's own verdict on this result, independent of ours.
   * Preferred from the lab's explicit interpretation/abnormal flag (HL7 OBX-8);
   * derived from its printed range only when it sent no flag.
   */
  labFlag: 'high' | 'low' | 'abnormal' | null;
  /**
   * Set when the lab reported a bound rather than a measurement ("<7.00").
   * `canonicalValue` then holds the bound, and the true value lies somewhere in
   * `direction` of it — so consumers must render a region, never a point.
   */
  censored: { bound: number; direction: 'below' | 'above' } | null;
}

/**
 * High and low share a colour on purpose. Direction is carried by the label,
 * which is the encoding that survives colour-blindness and greyscale printing;
 * giving "low" a second hue would read as a different severity rather than a
 * different direction. Anything rendering these must therefore always ship the
 * label alongside the colour — never a bare coloured marker.
 */
const STATUS_PRESENTATION: Record<BiomarkerStatus, { label: string; color: string; softBackground: string }> = {
  // Tints are the v3 status hues at low alpha — Botanical #545E00 and Signal
  // #9B2F2A — re-derived when the palette moved (docs/BRAND_IDENTITY_V3.md).
  // They must stay in step with --m-positive / --m-attention in index.css:
  // these are literals because a range bar composites them over the card.
  in_range: { label: 'In range', color: 'var(--m-positive)', softBackground: 'rgba(84, 94, 0, 0.14)' },
  high: { label: 'High', color: 'var(--m-attention)', softBackground: 'rgba(155, 47, 42, 0.12)' },
  low: { label: 'Low', color: 'var(--m-attention)', softBackground: 'rgba(155, 47, 42, 0.12)' },
  unknown: { label: '', color: 'var(--melio-text-muted)', softBackground: 'transparent' },
};

/** Normalise a unit string for comparison: lowercase, strip spaces, unify the micro sign. */
function normaliseUnit(unit?: string | null): string {
  return (unit || '')
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[μµ]/g, 'u');
}

/**
 * Convert a measured value to the biomarker's canonical unit. Returns the value
 * plus whether the unit could be reconciled. Mirrors engine `normaliseValue`.
 */
function toCanonicalValue(
  key: string,
  value: number,
  recordUnit: string | null | undefined,
  canonicalUnit: string,
): { value: number; unitOk: boolean } {
  const u = normaliseUnit(recordUnit);
  const canon = normaliseUnit(canonicalUnit);
  if (!u) return { value, unitOk: true }; // no unit recorded — assume canonical (best effort)
  if (u === canon) return { value, unitOk: true };

  switch (key) {
    case 'apo_b':
      if (u === 'g/l' || u === 'g/dl') return { value: value * 100, unitOk: true };
      break;
    case 'ldl_c':
    case 'hdl_c':
    case 'non_hdl_c':
    case 'total_cholesterol':
      if (u === 'mg/dl') return { value: value / 38.67, unitOk: true };
      break;
    case 'triglycerides':
      if (u === 'mg/dl') return { value: value / 88.57, unitOk: true };
      break;
    case 'lp_a':
      if (u === 'mg/dl') return { value: value * 2.5, unitOk: true };
      break;
    case 'hba1c':
      if (u === 'mmol/mol') return { value: value / 10.929 + 2.15, unitOk: true };
      break;
    default:
      break;
  }
  return { value, unitOk: false }; // mismatched unit with no known converter
}

/**
 * Strictly parse a numeric value. `valueNumber` wins; otherwise the string value
 * must be a plain number — deliberately NOT parsing "> 90", "< 7", "HAEMOLYSED",
 * so those degrade to value-only rather than a misleading marker.
 */
export function parseNumericValue(record: { valueNumber?: number | null; value?: string | null }): number | null {
  const measured = parseMeasuredValue(record);
  return measured && !measured.comparator ? measured.value : null;
}

/** Direction a censored result lies in, relative to its reported bound. */
export type Comparator = '<' | '<=' | '>' | '>=';

export interface MeasuredValue {
  /** The number the lab printed — for a censored result, the detection bound. */
  value: number;
  /** Set when the lab reported a bound rather than a measurement. */
  comparator: Comparator | null;
}

/**
 * Parse what the lab actually reported, including censored results.
 *
 * Pathology routinely reports a value outside the assay's measuring range as a
 * bound. Inuvi printed `Lipoprotein (a) <7.00`, `Thyroid Peroxidase Antibodies
 * <9.0` and `estimated GFR >90` on a single member's report. Every one was
 * discarded as "not a number", so three markers the lab did measure — all of
 * them reassuring — never reached the member at all.
 *
 * A bound is not a measurement, but it is usually enough to reach a verdict:
 * `<7.00` against a ceiling of 75 is unambiguously in range. `judgeCensored`
 * decides when that inference is sound; this function only reads the value.
 *
 * Mirrors `parseMeasuredValue` in functions/inuvi/hl7OruParser.js — the ingest
 * side of the same problem. Keep the two in step.
 */
export function parseMeasuredValue(
  record: { valueNumber?: number | null; value?: string | null; valueComparator?: string | null },
): MeasuredValue | null {
  const raw = (record.value ?? '').toString().trim();
  const match = /^(<=|>=|≤|≥|<|>)\s*(-?\d+(?:\.\d+)?)$/.exec(raw);
  if (match) {
    const symbol = match[1];
    const comparator: Comparator = symbol === '≤' ? '<=' : symbol === '≥' ? '>=' : (symbol as Comparator);
    const bound = Number.parseFloat(match[2]);
    if (Number.isFinite(bound)) return { value: bound, comparator };
  }
  // Ingest stores the bound in `valueNumber` with the symbol kept alongside, so
  // a censored result survives the round trip without re-parsing prose.
  if (typeof record.valueNumber === 'number' && Number.isFinite(record.valueNumber)) {
    const stored = record.valueComparator;
    const comparator = stored === '<' || stored === '<=' || stored === '>' || stored === '>=' ? stored : null;
    return { value: record.valueNumber, comparator };
  }
  if (!/^-?\d+(\.\d+)?$/.test(raw)) return null;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? { value: n, comparator: null } : null;
}

/**
 * Verdict for a censored result, or null when the bound cannot settle it.
 *
 * The inference is "does the entire region the true value could occupy fall on
 * one side of the range?". `<7.00` with a ceiling of 75 does — every value below
 * 7 is also below 75. `<7.00` against a range of 5–10 does not: the true value
 * might be 3 or might be 6, so we say nothing rather than guess.
 *
 * Concentrations cannot be negative, so a floor at or below zero counts as
 * satisfied — that is what makes `<9.0` against `0.0–20.0` answerable.
 */
export function judgeCensored(
  bound: number,
  comparator: Comparator,
  ref: RefRange,
): Exclude<BiomarkerStatus, 'unknown'> | null {
  const below = comparator === '<' || comparator === '<=';
  if (below) {
    // The whole region lies at or under `bound`.
    if (ref.low != null && bound <= ref.low) return 'low';
    const underCeiling = ref.high == null || bound <= ref.high;
    const aboveFloor = ref.low == null || ref.low <= 0;
    return underCeiling && aboveFloor ? 'in_range' : null;
  }
  // The whole region lies at or above `bound`.
  if (ref.high != null && bound >= ref.high) return 'high';
  const aboveFloor = ref.low == null || bound >= ref.low;
  return aboveFloor && ref.high == null ? 'in_range' : null;
}

/** A record as the member view sees it — the lab's own range and flag included. */
export interface StatusInput {
  valueNumber?: number | null;
  value?: string | null;
  unit?: string | null;
  /** The performing lab's printed reference range, in the record's own unit. */
  referenceRange?: { low?: number | null; high?: number | null; text?: string | null } | null;
  /** Lab's verdict, normalised at ingest (Inuvi HL7 OBX-8 → service.js). */
  interpretation?: string | null;
  /** Raw HL7 abnormal flag, when interpretation was not derived. */
  abnormalFlag?: string | null;
  /**
   * Whether the member was fasting, as reported by the lab. Selects between a
   * marker's fasting and non-fasting reference ranges; `null` selects neither.
   */
  fastingStatus?: boolean | null;
}

function unknownResult(
  meta: BiomarkerMeta,
  canonicalValue: number | null,
  labFlag: BiomarkerStatusResult['labFlag'],
  censored: BiomarkerStatusResult['censored'] = null,
): BiomarkerStatusResult {
  return {
    status: 'unknown',
    ...STATUS_PRESENTATION.unknown,
    hasBar: false,
    canonicalValue,
    ref: { low: null, high: null },
    rangeSource: null,
    axisMin: meta.axisMin,
    axisMax: meta.axisMax,
    axisUnit: meta.unit,
    labFlag,
    censored,
  };
}

function hasBound(ref: RefRange): boolean {
  return ref.low != null || ref.high != null;
}

/** The most permissive candidate — the one defensible whatever the draw condition was. */
function widestRange(ranges: RefRange[]): RefRange {
  if (ranges.length === 0) return { low: null, high: null };
  return ranges.reduce((widest, ref) => ({
    low: widest.low == null || ref.low == null ? null : Math.min(widest.low, ref.low),
    high: widest.high == null || ref.high == null ? null : Math.max(widest.high, ref.high),
  }));
}

/** Never 'unknown' — callers only reach here with a bounded range. */
function verdictFor(value: number, ref: RefRange): Exclude<BiomarkerStatus, 'unknown'> {
  if (ref.high != null && value > ref.high) return 'high';
  if (ref.low != null && value < ref.low) return 'low';
  return 'in_range';
}

/** The lab's printed range, kept in the record's own unit — no conversion. */
function labRefFrom(record: StatusInput): RefRange {
  const raw = record.referenceRange || {};
  return {
    low: typeof raw.low === 'number' && Number.isFinite(raw.low) ? raw.low : null,
    high: typeof raw.high === 'number' && Number.isFinite(raw.high) ? raw.high : null,
  };
}

/**
 * What the lab itself said. Their explicit flag wins over their printed range:
 * the flag is an assertion, whereas re-deriving from the range assumes the range
 * is in the same unit as the value — an assumption a transcription error can
 * break (one staging record stores free testosterone as 219.7 pmol/L against a
 * printed range of 0.198–0.619, which is nmol/L).
 */
function labFlagFrom(record: StatusInput, num: number | null): BiomarkerStatusResult['labFlag'] {
  const explicit = (record.interpretation || '').toString().trim().toLowerCase();
  if (explicit === 'high' || explicit === 'low' || explicit === 'abnormal') return explicit;
  const raw = (record.abnormalFlag || '').toString().trim().toUpperCase();
  if (raw === 'H' || raw === 'HH') return 'high';
  if (raw === 'L' || raw === 'LL') return 'low';
  if (raw === 'A' || raw === 'AA') return 'abnormal';

  if (num == null) return null;
  const ref = labRefFrom(record);
  if (!hasBound(ref)) return null;
  const derived = verdictFor(num, ref);
  return derived === 'in_range' ? null : derived;
}

/**
 * Share of the track the healthy band occupies on an axis derived from a range.
 *
 * The axis used to be the catalogue's `axisMin`/`axisMax`, ported from the
 * clinician catalogue: display maxima chosen for a reader who already knows the
 * scale. For a member they set the visual worst case, so the ratio of healthy
 * to unhealthy track became an artefact of that choice rather than a fact about
 * the member — 47 of the 65 markers with a Melio range drew a red-MAJORITY bar
 * for a perfectly healthy result. eGFR was the worst of them (range "90 and
 * above" on a 0–120 axis: three-quarters red, always) and a member read the
 * area, not the marker, and was frightened by his own normal result.
 *
 * Anchoring the band to a fixed share makes the geometry mean something: the
 * same proportion of green on every row, so the only thing that varies between
 * one marker and the next is where the member actually sits.
 */
const HEALTHY_BAND_SHARE = 0.6;

/** Track outside the healthy band, as a multiple of the band's own width. */
const OUTER_SHARE = (1 - HEALTHY_BAND_SHARE) / HEALTHY_BAND_SHARE;

/**
 * How far the axis may stretch past its ideal span to admit an out-of-range
 * value. Beyond this the value is left off-scale — the bar already draws a
 * chevron for that — rather than compressing the healthy band to a hairline: at
 * that distance the marker's exact position has stopped telling the member
 * anything the number itself does not.
 */
const MAX_AXIS_STRETCH = 3;

/** The catalogue's display scale. Only valid in the canonical unit. */
interface AxisAnchor {
  min: number;
  max: number;
}

/**
 * The axis the range alone implies, before any allowance for the value.
 *
 * `anchor` is passed ONLY on the Melio path. It is expressed in the canonical
 * unit and a lab-sourced range is in the lab's own, so applying it there would
 * reintroduce exactly the mis-scaling the axis fields exist to prevent. Without
 * an anchor a one-sided range has no second edge to size the band against, so
 * one is derived from the bound and the value.
 */
function idealAxis(value: number, ref: RefRange, anchor: AxisAnchor | null): { axisMin: number; axisMax: number } {
  const { low, high } = ref;

  if (low != null && high != null) {
    // A zero-width range still needs a non-degenerate axis, or axisFraction
    // divides by zero and pins the marker at the left edge.
    const width = high > low ? high - low : Math.max(Math.abs(high) * 0.35, 1);
    const pad = (width * OUTER_SHARE) / 2;
    return { axisMin: low - pad, axisMax: high + pad };
  }

  if (low != null) {
    // Open above ("90 and above"): the band runs from the floor to the top of
    // the scale, and only the approach to the floor is drawn below it.
    const top = anchor && anchor.max > low ? anchor.max : Math.max(value * 1.15, low * 1.25, low + 1);
    return { axisMin: low - (top - low) * OUTER_SHARE, axisMax: top };
  }

  if (high != null) {
    // Open below ("up to 1.7"): zero is the right bottom for a concentration,
    // which is every marker that carries a range of this shape.
    const bottom = anchor && anchor.min < high ? anchor.min : Math.min(0, value);
    return { axisMin: bottom, axisMax: high + (high - bottom) * OUTER_SHARE };
  }

  // Unbounded. Callers only reach the bar with a bounded range, but the axis
  // must still be non-degenerate if that ever stops being true.
  return { axisMin: value - 1, axisMax: value + 1 };
}

/**
 * The axis a bar is drawn on: the band-anchored ideal, widened just enough to
 * admit the value.
 *
 * An out-of-range value is plotted rather than pinned, because the bar now
 * encodes how far out a result is — the fill runs from the boundary to the
 * marker — and pinning would make "just below" and "dangerously below" draw
 * identically. That was the second half of the same complaint: a member could
 * see that something was outside the range but had no way to tell whether it
 * mattered.
 */
function deriveAxis(value: number, ref: RefRange, anchor: AxisAnchor | null): { axisMin: number; axisMax: number } {
  const ideal = idealAxis(value, ref, anchor);
  const idealSpan = ideal.axisMax - ideal.axisMin;
  const headroom = idealSpan * 0.08;

  let axisMin = value < ideal.axisMin ? value - headroom : ideal.axisMin;
  let axisMax = value > ideal.axisMax ? value + headroom : ideal.axisMax;

  if (axisMax - axisMin > idealSpan * MAX_AXIS_STRETCH) {
    // Keep the band where it is and give up on reaching the value.
    if (value < ideal.axisMin) axisMin = axisMax - idealSpan * MAX_AXIS_STRETCH;
    else axisMax = axisMin + idealSpan * MAX_AXIS_STRETCH;
  }

  // Nothing measured here can be negative, so an axis opening below zero spends
  // track on a region no result could ever occupy.
  const nonNegative = [value, ref.low, ref.high].every((n) => n == null || n >= 0);
  if (nonNegative && axisMin < 0) axisMin = 0;

  return axisMax > axisMin ? { axisMin, axisMax } : { axisMin, axisMax: axisMin + 1 };
}

export function computeBiomarkerStatus(
  record: StatusInput,
  meta: BiomarkerMeta,
  sex?: string | null,
): BiomarkerStatusResult {
  const measured = parseMeasuredValue(record);
  const labFlag = labFlagFrom(record, measured && !measured.comparator ? measured.value : null);
  if (measured == null) return unknownResult(meta, null, labFlag);

  const { value: num, comparator } = measured;

  // A censored result is judged by whether the whole region it could occupy
  // falls one side of the range; a measured one by simple comparison. Both run
  // the same Melio-then-lab precedence, so a "<7.00" is never treated as a
  // lesser class of result than a "7.00".
  const judge = (value: number, ref: RefRange): Exclude<BiomarkerStatus, 'unknown'> | null =>
    (comparator ? judgeCensored(value, comparator, ref) : verdictFor(value, ref));
  const censoredFor = (value: number): BiomarkerStatusResult['censored'] =>
    (comparator ? { bound: value, direction: comparator.startsWith('<') ? 'below' : 'above' } : null);

  // ── 1. Melio's range, where it can be applied to this member ──────────────
  const { value: canonical, unitOk } = toCanonicalValue(meta.key, num, record.unit, meta.unit);
  const candidates = resolveMelioRefCandidates(meta, sex, { fastingStatus: record.fastingStatus ?? null })
    .filter(hasBound);
  // With more than one candidate the sample condition is unknown, and only a
  // verdict every candidate agrees on may be asserted. The widest candidate is
  // reported as `ref` because it is the one defensible under either condition.
  const verdicts = candidates.map((candidate) => judge(canonical, candidate));
  const agreed = verdicts.length > 0 && verdicts.every((v) => v !== null && v === verdicts[0])
    ? verdicts[0]
    : null;
  const melioRef = widestRange(candidates);
  // Falls through when Melio's range cannot settle it — a censored bound landing
  // across a boundary, or candidates that disagree because the draw condition is
  // unknown. The lab's own range is often decisive where ours is not.
  if (unitOk && agreed) {
    return {
      status: agreed,
      ...STATUS_PRESENTATION[agreed],
      hasBar: true,
      canonicalValue: canonical,
      ref: melioRef,
      rangeSource: 'melio',
      // The catalogue's axis is the anchor, not the axis: it supplies the top of
      // the scale for a one-sided range, and deriveAxis sizes the rest around
      // the band. See HEALTHY_BAND_SHARE for why the raw scale was the problem.
      ...deriveAxis(canonical, melioRef, { min: meta.axisMin, max: meta.axisMax }),
      axisUnit: meta.unit,
      labFlag,
      censored: censoredFor(canonical),
    };
  }

  // ── 2. Fall back to the lab's printed range, in the lab's own unit ────────
  // Note this uses `num`, not `canonical`: when the unit could not be converted
  // the canonical value is meaningless, and the lab's range is expressed in the
  // very unit the record already carries.
  const labRef = labRefFrom(record);
  if (hasBound(labRef)) {
    const status = judge(num, labRef);
    if (status) {
      const { axisMin, axisMax } = deriveAxis(num, labRef, null);
      return {
        status,
        ...STATUS_PRESENTATION[status],
        hasBar: true,
        canonicalValue: num,
        ref: labRef,
        rangeSource: 'lab',
        axisMin,
        axisMax,
        axisUnit: record.unit || '',
        labFlag,
      censored: censoredFor(num),
      };
    }
  }

  // ── 3. Neither — show the value, assert nothing ───────────────────────────
  const shown = unitOk ? canonical : num;
  return unknownResult(meta, shown, labFlag, censoredFor(shown));
}
